import React from 'react';
import {observer} from 'mobx-react';

import {MOBILE_MAX_SCREEN_SIZE} from 'base/search.js';
import autobind from 'common/decorators/autobind.js';
import {PRAC_LOC_TYPE} from 'core/constants.js';
import {DirectoryStoreContext} from 'core/stores/RootStore.js';
import PracticeLocation from 'directory/components/PracticeLocation.js';
import ProfileCard from 'directory/components/ProfileCard.js';
import NoResultsNotice from 'directory/components/NoResultsNotice.js';
import {Notification} from '@HealthShareAU/hs-component-library';

export default
@observer
class SearchResults extends React.Component {
    static contextType = DirectoryStoreContext;

    constructor(props) {
        super(props);
        this.loadMoreTargetRef = React.createRef();
        // Infinite Scroll
        this.observer = new window.IntersectionObserver(this.loadMore, {
            threshold: 0,
        });
    }

    componentDidUpdate() {
        if (this.loadMoreTargetRef?.current) {
            this.observer.observe(this.loadMoreTargetRef.current);
        }
    }

    componentWillUnmount() {
        this.observer.disconnect();
    }

    isMobile() {
        return window.matchMedia(`(max-width: ${MOBILE_MAX_SCREEN_SIZE})`)
            .matches;
    }

    @autobind
    loadMore(entries) {
        const {locationSearchAPI} = this.context;
        entries.forEach((entry) => {
            if (entry.isIntersecting && !locationSearchAPI.isLoading) {
                if (locationSearchAPI.next) {
                    locationSearchAPI.loadMoreResults();
                }
            }
        });
    }

    renderResult(pracProps) {
        if (pracProps.type === PRAC_LOC_TYPE) {
            return <PracticeLocation {...pracProps} />;
        } else {
            return <ProfileCard {...pracProps} />;
        }
    }

    renderDistanceHeader(index) {
        const {
            specialty,
            rootStore: {
                paramStore: {isReferrals, isProUser},
            },
        } = this.context;

        const specialtyName =
            isReferrals || isProUser
                ? specialty.pluralProfessionalName
                : specialty.pluralName;
        let heading = `More ${specialtyName}`;
        let body = `These ${specialtyName} are more than 150km away.`;

        if (index === 0) {
            heading = `There are no ${specialtyName} close to your search location`;
            body = `These ${specialtyName} are more than 150km away, but may still be the closest to you.`;
        }

        return (
            <Notification customClass="distance-header" type="warning">
                <strong>{`${heading}.`}</strong> {body}
            </Notification>
        );
    }

    renderResults() {
        const {
            bannerAdsHaveLoaded,
            interest,
            results,
            specialty,
            telehealthDirectory,
            rootStore: {
                healthFundStore: {healthFund},
                paramStore: {isReferrals},
            },
        } = this.context;

        const distanceHeaderPosition = results.findIndex(
            (prac) => prac.distance > 150,
        );
        if (bannerAdsHaveLoaded) {
            return results?.map((practitioner, i) => {
                const pracProps = {
                    ...practitioner,
                    healthFund,
                    isReferrals,
                    specialty,
                    interest,
                };

                return (
                    <React.Fragment key={practitioner.id}>
                        {distanceHeaderPosition === i &&
                            !telehealthDirectory &&
                            this.renderDistanceHeader(i)}
                        {this.renderResult(pracProps)}
                        {i === results.length - 15 && (
                            <li ref={this.loadMoreTargetRef}></li>
                        )}
                    </React.Fragment>
                );
            });
        }
    }

    render() {
        const {
            bannerAdsHaveLoaded,
            locationSearchAPI,
            results,
            searchResultsError,
            rootStore: {
                paramStore: {isClient, hasSkin},
            },
        } = this.context;
        const {isLoading} = locationSearchAPI;

        const hfInfoHidden = hasSkin && !isClient;

        if (searchResultsError) {
            return (
                <NoResultsNotice>
                    <p>
                        {'An error has occurred while loading this content. '}
                        <a href={window.location.pathname}>
                            {'Please try again'}
                        </a>
                    </p>
                </NoResultsNotice>
            );
        }
        if (results?.length === 0 && !isLoading && bannerAdsHaveLoaded) {
            return (
                <NoResultsNotice>
                    <p>
                        {`No results fit your search criteria. Please update your search${
                            !hfInfoHidden ? ', selected health fund' : ''
                        } or filter options and try again.`}
                    </p>
                </NoResultsNotice>
            );
        }

        const props = {element: 'search-results'};
        if (this.isMobile()) {
            props.numCells = 1;
        }

        return (
            <section aria-busy={isLoading}>
                <ul>{this.renderResults()}</ul>
            </section>
        );
    }
}
